import React from "react";
import { useTranslation } from "react-i18next";
import { makeStyles } from "tss-react/mui";

import { PrimaryOutlinedButton } from "../CustomButton";

import { ControlledSelectField } from "./SelectField/SelectField";
import type { ControlledSelectValue, SelectOption } from "./SelectField/SelectField.types";

const useStyles = makeStyles()((theme) => ({
    row: { display: "flex", alignItems: "flex-start", gap: theme.spacing(2) },
    select: { flex: 1, minWidth: 0 },
}));

type Props = {
    /**
     * The form field holding the id of the selected file.
     */
    name: string;
    label: string;
    options: SelectOption[];
    loading?: boolean;
    disabled?: boolean;
    fullWidth?: boolean;
    onChange?: (value: ControlledSelectValue<false>) => void;
    /**
     * The extensions the file dialog offers, without the dot; omit to offer every file.
     */
    extensions?: string[];
    /**
     * Why the upload button is disabled. It doubles as the button's tooltip, which is why the reason is passed
     * rather than a boolean.
     */
    uploadDisabledReason?: string;
    /**
     * Disables the upload button for a reason of the caller's own, on top of `uploadDisabledReason`.
     */
    uploadDisabled?: boolean;
    isUploading?: boolean;
    /**
     * Replaces the upload button's tooltip while `isUploading`, to say what the button is waiting for.
     */
    uploadingTooltip?: string;
    /**
     * Called with the file the user picked in the file dialog. Uploading it, and pointing the field at the
     * result, is the caller's job.
     */
    onFilePicked: (file: File) => void;
    testId?: string;
};

/**
 * A select naming a file of the workspace, with an upload button that adds one the workspace does not have yet.
 */
export const FileSelectRow = (props: Props) => {
    const { classes } = useStyles();
    const { t } = useTranslation(["common"]);
    const inputRef = React.useRef<HTMLInputElement>(null);

    const onInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        // The same file picked twice in a row fires no change event unless the input is cleared.
        event.target.value = "";
        if (file) {
            props.onFilePicked(file);
        }
    };

    return (
        <div className={classes.row}>
            <div className={classes.select}>
                <ControlledSelectField
                    name={props.name}
                    label={props.label}
                    options={props.options}
                    loading={props.loading}
                    disabled={props.disabled || props.isUploading}
                    fullWidth={props.fullWidth}
                    onChange={props.onChange}
                />
            </div>
            <PrimaryOutlinedButton
                onClick={() => inputRef.current?.click()}
                disabled={Boolean(props.uploadDisabledReason) || props.uploadDisabled}
                loading={props.isUploading}
                startIcon={<i className="fa-solid fa-upload" />}
                tooltipProps={{
                    title:
                        props.uploadDisabledReason ??
                        (props.isUploading ? props.uploadingTooltip : undefined) ??
                        t("common:section.uploadField.tooltip"),
                }}
            >
                {t("common:button.upload")}
            </PrimaryOutlinedButton>
            <input
                ref={inputRef}
                type="file"
                accept={props.extensions?.map((extension) => `.${extension}`).join(",")}
                hidden
                data-testid={props.testId}
                onChange={onInputChange}
            />
        </div>
    );
};
